import React, {useCallback, useState} from 'react';

import PlateSchemaStepperForm from 'analysis-tool/data-frame-creation/schemas/PlateSchemaStepperForm';
import {FormState} from 'analysis-tool/data-frame-creation/schemas/RegistrySchemaDataFrameCreationTypes';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {setValueInConfig} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {CustomComponentEditorProps} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import useQueryApiIntrospection from 'query-api/useQueryApiIntrospection';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {buildQueryStringFromFormState, notifyIfFieldCountExceeded} from './LookUpQueryUtils';
import {useDataFrameNameStateBridge} from './useDataFrameNameStateBridge';

export default function PlateSchemaQueryEditor({
  config,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const [queryFormState, setQueryFormState] = useState<FormState>({entityListingFieldName: null});
  const [selectedPlateIds, setSelectedPlateIds] = useState<Array<string>>([]);
  const queryApiIntrospection = useQueryApiIntrospection();

  const syncConfigFromFormState = useCallback(
    (newFormState: FormState, newPlateIds: Array<string>) => {
      const queryString = buildQueryStringFromFormState(newFormState);
      let updatedConfig = setValueInConfig(config, ['query'], queryString);
      updatedConfig = setValueInConfig(updatedConfig, ['plateIds'], newPlateIds);
      onChangeConfig(updatedConfig, ['queryDescription'], '');
    },
    [config, onChangeConfig]
  );

  const handleSetQueryFormState = useCallback(
    (newFormState: FormState) => {
      if (notifyIfFieldCountExceeded(newFormState)) {
        return;
      }
      setQueryFormState(newFormState);
      syncConfigFromFormState(newFormState, selectedPlateIds);
    },
    [selectedPlateIds, syncConfigFromFormState]
  );

  const handleSetSelectedPlateIds = useCallback(
    (newPlateIds: Array<string>) => {
      setSelectedPlateIds(newPlateIds);
      syncConfigFromFormState(queryFormState, newPlateIds);
    },
    [queryFormState, syncConfigFromFormState]
  );

  const {dataFrameNameState, handleSetDataFrameNameState} = useDataFrameNameStateBridge(
    config,
    onChangeConfig
  );

  if (queryApiIntrospection.status === QueryResultType.ERROR) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading plate schemas." />;
  }
  if (queryApiIntrospection.status === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <PlateSchemaStepperForm
      queryApiIntrospection={queryApiIntrospection}
      queryFormState={queryFormState}
      setQueryFormState={handleSetQueryFormState}
      selectedPlateIds={selectedPlateIds}
      setSelectedPlateIds={handleSetSelectedPlateIds}
      dataFrameNameState={dataFrameNameState}
      setDataFrameNameState={handleSetDataFrameNameState}
      disabled={disabled}
    />
  );
}
